import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import Dotdotdot from 'react-dotdotdot';
import Icon from 'componentsV2/Icon';
import ProgressBar from 'componentsV2/ProgressBar';
import Badge from 'componentsV2/Badge';
import useBreakpoint from 'hooks/useBreakpoint';
import { isMobileBreakpoint, isTabletBreakpoint } from 'helpers/BreakpointHelper';
import { isNationEntitled } from 'helpers/ContentHelper';
import Style from './ContinueWatchingCTA.module.scss';

const ContinueWatchingCTA = ({ continueWatchingData, latestBookmark, onClose }) => {
  const [isClosed, setIsClosed] = useState(false);
  const path = window.location.pathname;
  const isWatchPage = path.includes('/watch');
  const isLivePage = path.includes('/live');
  const videoImgUrl = continueWatchingData?.images?.still?.raw;
  const videoImgAltText = continueWatchingData?.images?.still?.altText;
  const breakpoint = useBreakpoint();
  const isPortableDevice = isMobileBreakpoint(breakpoint) || isTabletBreakpoint(breakpoint);

  const noRender =
    !continueWatchingData ||
    isClosed ||
    isWatchPage ||
    isPortableDevice ||
    isLivePage ||
    !isNationEntitled();

  useEffect(() => {
    if (noRender) return;
    handleAnalytics({
      event: Events.CONTINUE_WATCHING_WIDGET,
      payload: {
        page_name: 'delta:web:landing: continue watching widget',
        page_type: 'continue watching widget',
      },
    });
  }, [noRender]);

  if (noRender) return null;

  const handleOnClose = () => {
    onClose();
    setIsClosed(true);
  };

  const linkProps = {
    to: `/watch/${continueWatchingData?.id}`,
    onClick: () => {
      handleAnalytics({
        event: Events.BUTTON_LINK_SELECTED,
        payload: {
          page_button_text: 'play',
          page_modal_type: 'continue watching widget',
          page_name: 'delta:web: continue watching widget',
        },
      });
      onClose();
    },
  };

  return (
    <div className={Style.continueWatchingContainer}>
      <div
        className={Style.closeIcon}
        onClick={() => {
          handleAnalytics({
            event: Events.BUTTON_LINK_SELECTED,
            payload: {
              page_button_text: 'close',
              page_modal_type: 'continue watching widget',
              page_name: 'delta:web: continue watching widget',
            },
          });
          handleOnClose();
        }}
      >
        <Icon icon="close" color="white" />
      </div>
      <div className={Style.ctaContainer}>
        <Link className={Style.videoImage} {...linkProps}>
          <Badge theme={Style} type="continue-watching-cta" text="Continue Watching" />
          <img src={videoImgUrl} alt={videoImgAltText} />
          <Icon icon="play-circle" />
          <ProgressBar
            theme={Style}
            endDate={continueWatchingData?.endDate}
            percentWatched={latestBookmark?.percentWatched}
            isContinueWatching
          />
        </Link>
        <div className={Style.ctaText}>
          <Link className={Style.seriesName} {...linkProps}>
            <Dotdotdot clamp={2} useNativeClamp>
              {continueWatchingData?.seriesName}
            </Dotdotdot>
          </Link>
          <Link className={Style.episodeName} {...linkProps}>
            <Dotdotdot clamp={2} useNativeClamp>
              {continueWatchingData?.headline}
            </Dotdotdot>
          </Link>
        </div>
      </div>
    </div>
  );
};

ContinueWatchingCTA.propTypes = {
  continueWatchingData: PropTypes.shape({}),
  latestBookmark: PropTypes.shape({}),
  onClose: PropTypes.func,
};

export default ContinueWatchingCTA;
